<template>
  <CustomModal v-if="!!transaction" @close="$emit('close')" :position="position">
    <template v-slot:body>
      <div class="bg-block rounded -m-8 -m-12 grid grid-cols-12 transaction-edit-modal">
        <div class="col-span-3
                    p-8 lg:p-12 lg:pb-6 lg:pl-14
                    bg-transaction-gradient">
          <div class="text-xl font-bold text-text leading-tight ">
            {{ $t('editTitle') }}
          </div>
        </div>
        <div class="col-span-9 pt-8 pl-8 pr-8 lg:pt-11 lg:pl-12 lg:pr-17">
          <div class="inline-flex items-center space-x-4">
            <TransactionOptionsItem v-if="transaction.status !== 0"
                                    v-for="(option,i) in availableOptions"
                                    :key="i"
                                    @click="toggleOption(option)"
                                    :active="options[option]"
                                    :option="option"/>
            <div v-if="transactionCanBeTransformed" class="operation-options_item"
                 v-tooltip="$t('splitTooltip')"
                 @click="$emit('transformToTransfer')">
              <IconSplit class="text-icon transition duration-200"/>
            </div>
          </div>
          <div v-if="transactionAuthor"
               class="text-sm text-text-ternary leading-10 float-right mr-8 max-w-xs truncate">
            {{ transactionAuthor }}
          </div>
        </div>
        <component :is="`transaction-edit-form-${formType}`" class="col-span-12"
                   :transaction_options="options"
                   :transaction_item="transaction"
                   :submitted="submitted"
                   @cancel="$emit('close')"
                   @updateTransfer="saveTransfer($event)"
                   @removeTransfer="rmTransfer($event)"
                   @updateTransaction="saveTransaction($event)"
                   @removeTransaction="rmTransaction($event)"/>
      </div>
    </template>
  </CustomModal>
</template>

<script>
import CustomModal from "@/components/UI/modals/CustomModal";
import TransactionOptionsItem from "@/components/common/Transaction/TransactionOptions/TransactionOptionsItem";
import TransactionEditFormDefault
  from "@/components/common/Transaction/TransactionEditForm/TransactionEditFormDefault";
import TransactionEditFormTransfer
  from "@/components/common/Transaction/TransactionEditForm/TransactionEditFormTransfer";
import TransactionEditFormSystem
  from "@/components/common/Transaction/TransactionEditForm/TransactionEditFormSystem";
import {mapActions, mapGetters} from "vuex";
import IconSplit from "@/components/icons/IconSplit";

export default {
  name: "TransactionEditModal",
  components: {
    IconSplit,
    CustomModal,
    TransactionOptionsItem,
    TransactionEditFormDefault,
    TransactionEditFormTransfer,
    TransactionEditFormSystem
  },
  props: {
    transaction: {
      required: true
    },
    position: {
      type: String,
      default: 'relative'
    }
  },
  data() {
    return {
      submitted: false,
      options: {
        agent: false,
        comment: false,
        tags: false,
        category: false,
      }
    }
  },
  computed: {
    ...mapGetters('space', ['spaceInfo']),
    isSystemTransaction() {
      return this.transaction.is_system;
    },
    availableOptions() {
      if (this.isSystemTransaction) return []
      const defaultOptions = ['comment', 'tags'],
          transferMoreOptions = ['category'],
          agentAvailableOptions = ['agent'];

      let options = [...defaultOptions];

      if (!!this.transaction.transfer_id) {
        options = [...options, ...transferMoreOptions]
      } else {
        if (this.$store.getters['space/spaceUseContractors']) options = [...agentAvailableOptions, ...options]
      }
      return options;
    },
    formType() {
      return this.isSystemTransaction
          ? 'system'
          : !!this.transaction.transfer_id
              ? 'transfer'
              : 'default'
    },
    transactionCanBeTransformed() {
      return !this.isSystemTransaction && !this.transaction.transfer_id
    },
    transactionAuthor() {
      if (!this.spaceInfo.author_enabled) return false;

      const {user} = this.transaction;
      return user || this.$t('authorBankIntegration');
    }
  },
  methods: {
    ...mapActions('space/transactions', ['updateTransaction', 'removeTransaction']),
    ...mapActions('space/transfers', ['updateTransfer', 'removeTransfer']),
    checkTransactionOptions() {
      if (!!this.transaction.transfer_id) {
        this.options.comment = !!this.transaction.comment;
        this.options.tags = !!this.transaction.tags.length;
        this.options.category = !!this.transaction.transfer.expense.expense;
      } else {
        this.options.agent = !!this.transaction.contractor_id;
        this.options.comment = !!this.transaction.comment;
        this.options.tags = !!this.transaction.tags.length;
      }
    },
    toggleOption(opt) {
      this.options[opt] = !this.options[opt]
    },
    async saveTransaction(data) {
      this.submitted = true;
      try {
        const res = await this.updateTransaction(data)
        if (res.body.success) {
          this.$toasted.success(this.$t('changesSaved'))
          this.$emit('success')
          this.$emit('close')
        } else {
          this.$toasted.error(this.$t('checkFields'))
        }
      } catch (e) {
        this.$toasted.error(this.$t('errorOccured'))
        console.log(e)
      } finally {
        this.submitted = false
      }
    },
    async saveTransfer(data) {
      this.submitted = true;
      try {
        const res = await this.updateTransfer(data)
        if (res.body.success) {
          this.$toasted.success(this.$t('changesSaved'))
          this.$emit('success')
          this.$emit('close')
        } else {
          this.$toasted.error(this.$t('checkFields'))
        }
      } catch (e) {
        this.$toasted.error(this.$t('errorOccured'))
        console.log(e)
      } finally {
        this.submitted = false
      }
    },
    async rmTransaction(id) {
      this.submitted = true;
      try {
        const res = await this.removeTransaction(id)
        if (res.body.success) {
          this.$toasted.success(this.$t('transactionRemoved'))
          this.$emit('success')
          this.$emit('close')
        }
      } catch (e) {
        this.$toasted.error(this.$t('errorOccured'))
        console.log(e)
      } finally {
        this.submitted = false
      }
    },
    async rmTransfer(id) {
      this.submitted = true;
      try {
        const res = await this.removeTransfer(id)
        if (res.body.success) {
          this.$toasted.success(this.$t('transactionRemoved'))
          this.$emit('success')
          this.$emit('close')
        }
      } catch (e) {
        this.$toasted.error(this.$t('errorOccured'))
        console.log(e)
      } finally {
        this.submitted = false
      }
    }
  },
  watch: {
    transaction: {
      handler(newVal) {
        if (!!newVal) this.checkTransactionOptions()
      }
    }
  },
  i18n: {
    messages: {
      en: {
        editTitle: 'Edit',
        changesSaved: 'Changes saved',
        errorOccurred: 'Error occurred',
        transactionRemoved: 'Operation removed',
        checkFields: 'Check fields',
        splitTooltip: 'Converting an operation to a transfer',
        authorBankIntegration: 'Imported from bank'
      },
      ru: {
        editTitle: 'Редактирование',
        changesSaved: 'Изменения сохранены',
        errorOccurred: 'Произошла ошибка',
        transactionRemoved: 'Операция удалена',
        checkFields: 'Проверьте поля',
        splitTooltip: 'Преобразование операции в перевод',
        authorBankIntegration: 'Импорт из банка'
      }
    }
  }
}
</script>

<style>
.transaction-edit-modal {
  width: 820px;
}
</style>